// Add-ons: READ + REQUEST (commercial packaging Phase 3). The old direct
// toggle wrote entitlements through an unvalidated endpoint with no audit or
// charge — closed. Add-ons now show their entitled state; enabling one is a
// commercial change made by KaziPeople (plan version, subscription item, or
// override) through the audited entitlement lifecycle.
import React from 'react';
import { Briefcase, Mail } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { useEntitlements } from '@/features/entitlements/useEntitlements';

interface AddOn {
  code: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}

const ADD_ONS: AddOn[] = [
  {
    code: 'commission',
    name: 'Commissions — non-employee payments',
    description:
      'Pay agents, contractors, delivery and owner-operator partners for completed work. Records activities, calculates commission with country withholding, and pays the net through PayGate. Separate from payroll; not a payslip.',
    icon: <Briefcase className="h-5 w-5" />,
  },
];

export function AddOnsCard() {
  const { hasProductStrict } = useEntitlements();

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div>
          <h2 className="text-base font-semibold text-foreground">Add-ons</h2>
          <p className="text-sm text-muted-foreground">
            Optional KaziPeople packages. Included add-ons are part of your subscription — to add one, contact KaziPeople and it is enabled on your agreement.
          </p>
        </div>
        {ADD_ONS.map((a) => {
          const on = hasProductStrict(a.code);
          return (
            <div key={a.code} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-4">
              <div className="flex items-start gap-3 min-w-0">
                <span className="text-orange-500 mt-0.5">{a.icon}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground">{a.name}</p>
                    <Badge variant={on ? 'default' : 'outline'} className="text-[10px]">{on ? 'Included' : 'Not included'}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                </div>
              </div>
              {!on && (
                <Button size="sm" variant="outline" asChild>
                  <a href={`mailto:sales@kazipeople.com?subject=${encodeURIComponent(`Add-on request: ${a.name}`)}`}>
                    <Mail className="mr-1 h-4 w-4" />Request add-on
                  </a>
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
